import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReCAPTCHA from 'react-google-recaptcha';
import { useRegister } from '@/hooks/useAuth';
import {
  MIN_PASSWORD_LENGTH,
  validatePhone,
  validateEmail,
  validatePasswordStrength,
  type ParticipantGender,
} from 'shared';
import PasswordInput from '@/components/PasswordInput';
import ReCaptcha from '@/components/ReCaptcha';
import { registerErrorMessage, passwordRequirementsMessage } from '@/lib/errors';

const GENDER_OPTIONS: ReadonlyArray<{ value: ParticipantGender; label: string }> = [
  { value: 'F', label: 'Feminino' },
  { value: 'M', label: 'Masculino' },
  { value: 'I', label: 'Outro' },
];

type RegisterField =
  | 'nome'
  | 'apelido'
  | 'genero'
  | 'dataNascimento'
  | 'contacto'
  | 'email'
  | 'telemovel'
  | 'password'
  | 'confirmPassword'
  | 'captcha';

type FieldErrors = Partial<Record<RegisterField, string>>;

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1.5 text-xs font-medium leading-relaxed text-destructive">
      {message}
    </p>
  );
}

function BackArrowIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M19 12H5m0 0l7 7m-7-7l7-7" />
    </svg>
  );
}

export default function RegisterPage() {
  const [nome, setNome] = useState('');
  const [apelido, setApelido] = useState('');
  const [genero, setGenero] = useState<ParticipantGender | ''>('');
  const [dataNascimento, setDataNascimento] = useState('');
  const [email, setEmail] = useState('');
  const [telemovel, setTelemovel] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  const [pendingId, setPendingId] = useState<number | null>(null);
  const register = useRegister();
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFieldErrors({});

    const trimmedEmail = email.trim();
    const trimmedPhone = telemovel.trim();
    const errors: FieldErrors = {};

    if (!nome.trim()) {
      errors.nome = 'O nome é obrigatório.';
    }

    if (!apelido.trim()) {
      errors.apelido = 'O apelido é obrigatório.';
    }

    if (!genero) {
      errors.genero = 'Selecione o género.';
    }

    if (!dataNascimento) {
      errors.dataNascimento = 'A data de nascimento é obrigatória.';
    }

    if (!trimmedEmail && !trimmedPhone) {
      errors.contacto =
        'Indique um email ou telemóvel para poder recuperar a sua palavra-passe.';
    }

    if (trimmedEmail && !validateEmail(trimmedEmail).valid) {
      errors.email = 'O formato do email é inválido (ex.: nome@exemplo.com).';
    }

    if (trimmedPhone && !validatePhone(trimmedPhone).valid) {
      errors.telemovel =
        'O número de telemóvel é inválido. Use apenas dígitos e os símbolos + ( ) - e es';
    }

    const passwordError = passwordRequirementsMessage(validatePasswordStrength(password));
    if (passwordError) {
      errors.password = passwordError;
    }

    if (password !== confirmPassword) {
      errors.confirmPassword = 'As palavras-passe não coincidem.';
    }

    const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
    if (siteKey && !captchaToken) {
      errors.captcha = 'Conclua o captcha para continuar.';
    }

    const messages = Object.values(errors);
    if (messages.length > 0) {
      setFieldErrors(errors);
      setFormError(
        messages.length === 1 ? messages[0] : 'Corrija os campos assinalados abaixo.'
      );
      return;
    }

    if (!genero) {
      // Unreachable (errors.genero above); narrows the union for the payload.
      return;
    }

    register.mutate(
      {
        nome: nome.trim(),
        apelido: apelido.trim(),
        genero,
        dataNascimento,
        email: trimmedEmail || undefined,
        telemovel: trimmedPhone || undefined,
        password,
        captchaToken: captchaToken || undefined,
      },
      {
        onSuccess: (result) => {
          if (result.status === 'pending') {
            // Every new account waits for approval: never sign in — show the
            // awaiting-approval dialog and let the user dismiss it to /login.
            setPendingId(result.participantId);
            return;
          }
          navigate('/');
        },
        onError: () => {
          recaptchaRef.current?.reset();
          setCaptchaToken(null);
        },
      }
    );
  };

  const handlePendingDismiss = () => {
    setPendingId(null);
    navigate('/login', { replace: true });
  };

  const hasBannerError = !!(formError || register.isError);

  return (
    <>
      <Helmet>
        <title>Criar conta | AICD</title>
      </Helmet>
      <div className="relative min-h-screen overflow-hidden bg-background">
        <span
          className="phone-bg-blob"
          style={{
            width: 260,
            height: 260,
            top: -60,
            right: -80,
            backgroundColor: 'hsla(var(--accent-purple), 0.12)',
          }}
          aria-hidden="true"
        />

        <div className="relative z-10 mx-auto w-full max-w-md px-6 pt-8 pb-12">
          <button
            type="button"
            className="back-link"
            onClick={() => navigate('/login')}
            aria-label="Voltar ao iní"
          >
            <BackArrowIcon />
            Voltar
          </button>

          <header className="mt-2">
            <h1 className="text-[26px] font-extrabold tracking-tight text-foreground">
              Criar conta
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Registe-se para reservar equipamento no seu Centro Digital.
            </p>
            <p className="mt-2 text-sm">
              <Link to="/register/ee" className="font-semibold text-primary underline-offset-2 hover:underline">
                É Encarregado de Educação? Peça aqui a sua conta.
              </Link>
            </p>
          </header>

          <form
            className="mt-8 flex flex-col gap-4"
            onSubmit={handleSubmit}
            aria-busy={register.isPending}
            noValidate
          >
            <div role="alert" aria-live="assertive" aria-atomic="true">
              {hasBannerError && (
                <div
                  id="register-error"
                  className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive"
                >
                  {formError || registerErrorMessage(register.error)}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="nome" className="label">
                  Nome
                </label>
                <input
                  id="nome"
                  type="text"
                  autoComplete="given-name"
                  required
                  className="input"
                  placeholder="Ana"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  aria-invalid={!!fieldErrors.nome || undefined}
                  aria-describedby={fieldErrors.nome ? 'nome-error' : undefined}
                />
                <FieldError id="nome-error" message={fieldErrors.nome} />
              </div>
              <div>
                <label htmlFor="apelido" className="label">
                  Apelido
                </label>
                <input
                  id="apelido"
                  type="text"
                  autoComplete="family-name"
                  required
                  className="input"
                  placeholder="Silva"
                  value={apelido}
                  onChange={(e) => setApelido(e.target.value)}
                  aria-invalid={!!fieldErrors.apelido || undefined}
                  aria-describedby={fieldErrors.apelido ? 'apelido-error' : undefined}
                />
                <FieldError id="apelido-error" message={fieldErrors.apelido} />
              </div>
            </div>

            <fieldset>
              <legend className="label">Género</legend>
              <div
                className="mt-1 flex flex-wrap gap-4"
                role="radiogroup"
                aria-required="true"
                aria-invalid={!!fieldErrors.genero || undefined}
                aria-describedby={fieldErrors.genero ? 'genero-error' : undefined}
              >
                {GENDER_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 text-sm font-medium text-foreground"
                  >
                    <input
                      type="radio"
                      name="genero"
                      value={option.value}
                      checked={genero === option.value}
                      onChange={() => setGenero(option.value)}
                      required
                      className="accent-primary"
                    />
                    {option.label}
                  </label>
                ))}
              </div>
              <FieldError id="genero-error" message={fieldErrors.genero} />
            </fieldset>

            <div>
              <label htmlFor="dataNascimento" className="label">
                Data de nascimento
              </label>
              <input
                id="dataNascimento"
                type="date"
                autoComplete="bday"
                required
                className="input"
                value={dataNascimento}
                onChange={(e) => setDataNascimento(e.target.value)}
                aria-invalid={!!fieldErrors.dataNascimento || undefined}
                aria-describedby={fieldErrors.dataNascimento ? 'dataNascimento-error' : 'dob-hint'}
              />
              <FieldError id="dataNascimento-error" message={fieldErrors.dataNascimento} />
              <p id="dob-hint" className="form-hint mt-1.5">
                Formato: AAAA-MM-DD
              </p>
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className="input"
                placeholder="nome@exemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={!!(fieldErrors.email || fieldErrors.contacto) || undefined}
                aria-describedby={
                  fieldErrors.email
                    ? 'email-error'
                    : fieldErrors.contacto
                      ? 'contacto-error'
                      : 'contact-hint'
                }
              />
              <FieldError id="email-error" message={fieldErrors.email} />
            </div>

            <div>
              <label htmlFor="telemovel" className="label">
                Telemóvel
              </label>
              <input
                id="telemovel"
                type="tel"
                autoComplete="tel"
                className="input"
                placeholder="+351 912 345 678"
                value={telemovel}
                onChange={(e) => setTelemovel(e.target.value)}
                aria-invalid={!!(fieldErrors.telemovel || fieldErrors.contacto) || undefined}
                aria-describedby={
                  fieldErrors.telemovel
                    ? 'telemovel-error'
                    : fieldErrors.contacto
                      ? 'contacto-error'
                      : 'contact-hint'
                }
              />
              <FieldError id="telemovel-error" message={fieldErrors.telemovel} />
              <FieldError id="contacto-error" message={fieldErrors.contacto} />
              <p id="contact-hint" className="form-hint mt-1.5">
                É necessário email ou telemóvel para recuperar a palavra-passe.
              </p>
            </div>

            <div>
              <label htmlFor="password" className="label">
                Palavra-passe
              </label>
              <PasswordInput
                id="password"
                autoComplete="new-password"
                required
                placeholder={`Mín. ${MIN_PASSWORD_LENGTH} caracteres`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={!!fieldErrors.password || undefined}
                aria-describedby={`${fieldErrors.password ? 'password-error ' : ''}password-hint`}
              />
              <FieldError id="password-error" message={fieldErrors.password} />
              <p id="password-hint" className="form-hint mt-1.5">
                {`Pelo menos ${MIN_PASSWORD_LENGTH} caracteres, com maiúsculas, minúsculas e um número.`}
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">
                Confirmar palavra-passe
              </label>
              <PasswordInput
                id="confirmPassword"
                revealLabel="confirmação da palavra-passe"
                autoComplete="new-password"
                placeholder="Repita a palavra-passe"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                aria-invalid={!!fieldErrors.confirmPassword || undefined}
                aria-describedby={fieldErrors.confirmPassword ? 'confirmPassword-error' : undefined}
              />
              <FieldError id="confirmPassword-error" message={fieldErrors.confirmPassword} />
            </div>

            <div>
              <ReCaptcha
                ref={recaptchaRef}
                onChange={handleCaptchaChange}
                onExpired={handleCaptchaExpired}
              />
              <FieldError id="captcha-error" message={fieldErrors.captcha} />
            </div>

            <p className="text-xs text-muted-foreground">
              Ao criar conta, os seus dados são tratados como explicado em{' '}
              <Link to="/privacidade" className="font-semibold text-primary underline-offset-2 hover:underline">
                Privacidade
              </Link>
              .
            </p>

            <button
              type="submit"
              disabled={register.isPending}
              className="btn-secondary w-full mt-2"
            >
              {register.isPending ? 'A criar conta…' : 'Criar conta'}
            </button>

            <div className="form-hint form-hint--boxed mt-2">
              O seu ID numérico será gerado automaticamente após o registo.
            </div>
          </form>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            Já tem conta?{' '}
            <Link to="/login" className="font-semibold text-primary">
              Iniciar sessão
            </Link>
          </p>
        </div>

        {pendingId !== null && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pending-dialog-title"
            aria-describedby="pending-dialog-body"
          >
            <div className="w-full max-w-sm rounded-2xl bg-background p-6 shadow-2xl">
              <h2
                id="pending-dialog-title"
                className="text-lg font-bold text-foreground"
              >
                Registo pendente de aprovação
              </h2>
              <p
                id="pending-dialog-body"
                className="mt-3 text-sm text-muted-foreground"
              >
                O seu registo precisa de ser aprovado por um administrador antes
                de poder iniciar sessão.
              </p>
              <div className="mt-4 rounded-xl bg-muted px-4 py-3 text-center">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  O seu identificador
                </p>
                <p className="mt-1 text-2xl font-extrabold text-foreground">
                  {pendingId}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Guarde este número.
                </p>
              </div>
              <button
                type="button"
                onClick={handlePendingDismiss}
                className="btn-secondary w-full mt-5"
              >
                Continuar
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
